import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import Spacer from 'components/Spacer';
import MainContent from 'components/MainContent';
import PageLoader from 'components/PageLoader/PageLoader';
import { Message } from 'semantic-ui-react';
import { Button, TextField } from '@teachstonellc/component-library';
import certificationsSVG from 'images/dashboard/certification.svg';
import useProductKey from 'components/ActivateProductKey/useProductKey';
import TokenRedemptionDialog from 'components/ActivateProductKey/TokenRedemptionDialog';
import CertificationList from 'components/Certifications/CertificationList';
import TestList from 'components/Certifications/TestList';
import { useLocationState } from 'hooks/useLocationState';
import SecondaryButton from 'components/SecondaryButton';
import LoggedMessage from 'components/LoggedMessage';

import {
  HeroBackground,
  HeroContainer,
  HeroMain,
  HeroIcon,
  HeroLinks,
  MobileProductKey,
  MobileButton,
  ProductKey,
  ProductKeyForm,
  Title,
  Subtitle,
  Label,
  TextFieldWrapper,
  MessageWrapper
} from './Styled';

type LocationState = {
  showRedeemTokenError: boolean;
  showSuccess: boolean;
  tokenType: string;
  message?: string;
};

const Certifications: React.FC = (...props) => {
  const pKeyRequest = useProductKey();
  const [productKey, setProductKey] = useState('');
  const state = useLocationState<LocationState>();
  const [showMessage, setShowMessage] = useState(state?.showSuccess);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Used to trigger a reload on certifications when a CGC token is used.
  const [timestamp, setTimestamp] = useState(Date.now());

  // The location state will change if a CGC token is successfully used.
  useEffect(() => {
    if (state?.showSuccess && pKeyRequest.isSuccess) {
      setProductKey('');
      setShowMessage(true);
      setTimestamp(Date.now());
    }
  }, [state, pKeyRequest.isSuccess]);

  // Only one of these inputs is visible at a time (desktop vs. mobile
  // breakpoint, toggled via CSS `display: none`); offsetParent is null
  // for a display:none element, so this picks out whichever is rendered.
  useEffect(() => {
    if (pKeyRequest.isError) {
      const visibleInput = [
        desktopInputRef.current,
        mobileInputRef.current
      ].find(input => input && input.offsetParent !== null);

      visibleInput?.focus();
    }
  }, [pKeyRequest.isError, submitAttempt]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (pKeyRequest.isLoading) {
      return;
    }

    if (isEmpty(e.target.value)) {
      pKeyRequest.reset();
    }

    setProductKey(e.target.value);
  }

  function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
  }

  function handleSubmit() {
    window.localStorage.removeItem('unified_token');
    setSubmitAttempt(attempt => attempt + 1);
    pKeyRequest.submit(productKey);
  }

  function isRecertification(): boolean {
    return state?.tokenType === 'test_template';
  }

  function getStatusMessage(): string {
    if (
      isRecertification() &&
      state?.showSuccess &&
      state?.showRedeemTokenError
    ) {
      return i18n.ft(messages.recertificationFail);
    }

    if (isRecertification()) {
      if (state?.message) {
        return state.message;
      }

      return i18n.ft(messages.recertificationSuccess);
    }

    if (state?.tokenType === 'subscription_product_key') {
      return i18n.ft(messages.subscriptionProductKeySuccess);
    }

    return i18n.ft(messages.productKeySuccess);
  }

  return (
    <MainContent>
      <TokenRedemptionDialog
        confirmation={pKeyRequest.pendingConfirmation}
        isLoading={pKeyRequest.isLoading}
        onConfirm={pKeyRequest.confirmTokenRedemption}
        onCancel={pKeyRequest.cancelTokenRedemption}
      />
      <HeroBackground>
        <HeroContainer>
          <HeroIcon alt={i18n.ft(messages.iconAlt)} src={certificationsSVG} />
          <HeroMain>
            <Title>{i18n.ft(messages.header)}</Title>
            <Spacer size={16} />
            <Subtitle>{i18n.ft(messages.subheader)}</Subtitle>

            <ProductKey onSubmit={handleFormSubmit}>
              <Label htmlFor="productKey">
                {i18n.ft(messages.useProductKey)}
              </Label>
              <ProductKeyForm>
                <TextFieldWrapper>
                  <TextField
                    ref={desktopInputRef}
                    id="productKey"
                    label={i18n.ft(messages.useProductKey)}
                    showLabel={false}
                    value={productKey}
                    onChange={handleChange}
                    placeholder={i18n.ft(messages.enterProductKey)}
                    error={pKeyRequest.isError ? pKeyRequest.error : undefined}
                  />
                </TextFieldWrapper>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  loading={pKeyRequest.isLoading}
                  disabled={pKeyRequest.isLoading}
                >
                  {i18n.ft(messages.activateProductKey)}
                </Button>
              </ProductKeyForm>
            </ProductKey>

            <MobileProductKey onSubmit={handleFormSubmit}>
              <div>
                <Label htmlFor="productKeyMobile">
                  {i18n.ft(messages.useProductKey)}
                </Label>
                <TextField
                  ref={mobileInputRef}
                  id="productKeyMobile"
                  label={i18n.ft(messages.useProductKey)}
                  showLabel={false}
                  value={productKey}
                  onChange={handleChange}
                  placeholder={i18n.ft(messages.enterProductKey)}
                  error={pKeyRequest.isError ? pKeyRequest.error : undefined}
                />
              </div>

              <MobileButton>
                <Button
                  fullWidth
                  variant="primary"
                  onClick={handleSubmit}
                  loading={pKeyRequest.isLoading}
                  disabled={pKeyRequest.isLoading}
                >
                  {i18n.ft(messages.activateProductKey)}
                </Button>
              </MobileButton>
            </MobileProductKey>
          </HeroMain>

          <HeroLinks>
            <SecondaryButton
              fluid
              content={i18n.ft(messages.learnMore)}
              icon="arrow right"
              as="a"
              href="https://teachstone.com/class-certifications"
            />
            <Spacer size={12} />
            <SecondaryButton
              fluid
              content={i18n.ft(messages.engageCommunity)}
              icon="arrow right"
              as="a"
              href={RouteHelpers.getPath('community')}
            />
          </HeroLinks>
        </HeroContainer>
      </HeroBackground>

      {showMessage && (
        <MessageWrapper>
          <LoggedMessage
            positive={!state.showRedeemTokenError}
            negative={state.showRedeemTokenError}
            onDismiss={() => setShowMessage(false)}
            message={getStatusMessage()}
            severity={state.showRedeemTokenError ? 'error' : 'info'}
          >
            <Message.Header>{getStatusMessage()}</Message.Header>
          </LoggedMessage>
        </MessageWrapper>
      )}

      {pKeyRequest.isLoading ? <PageLoader /> : <TestList />}
      <CertificationList key={timestamp} />
    </MainContent>
  );
};

export default Certifications;
